import { DataGridPremium, type GridColDef, useGridApiRef } from '@mui/x-data-grid-premium'
import { formatCurrency } from 'helpers/utils'
import type React from 'react'
import { useMemo } from 'react'
import type { AccountResultDto } from '../api/swagger/definitions/backoffice'
import ColorsDeprecated from '../figma/tokens/ColorsDeprecated'
import useLanguage from '../flamingo/hooks/useLanguage'
import { TableHeader, useTableGridProps } from '../helpers/getTableOptions'
import TextKeys from '../libs/TextKeys'
import { getText } from '../tiger/libs/TextRepository'
import TableWrapper from './TableWrapper'

interface AccountingSummaryProps {
  summary: AccountResultDto[]
  loading: boolean
  currencies: string[]
}

type SummaryRow = AccountResultDto & { id: string | number }

const VouchersSummaryTable: React.FC<AccountingSummaryProps> = ({ summary, loading, currencies }) => {
  const language = useLanguage()
  const gridProps = useTableGridProps({ headerTextKey: TextKeys.accountingPaymentsOCR })
  const apiRef = useGridApiRef()

  // The grid needs a stable row id and AccountResultDto has no guaranteed
  // unique field, so fall back to the position when the account number is absent
  const rows = useMemo<SummaryRow[]>(() => summary.map((row, index) => ({ ...row, id: row.no ?? index })), [summary])

  const columns = useMemo<GridColDef<SummaryRow>[]>(
    () => [
      {
        headerName: getText(TextKeys.voucherListNameLabel, language),
        field: 'name',
        flex: 1,
      },
      {
        headerName: getText(TextKeys.voucherListNumberLabel, language),
        field: 'no',
        flex: 1,
      },
      ...currencies.map<GridColDef<SummaryRow>>((currency) => ({
        headerName: currency,
        field: currency,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => {
          const period = row.periods?.find((period) => period.currency === currency)

          if (period) {
            return formatCurrency({ value: period.value, currency })
          }

          return '-'
        },
      })),
    ],
    [currencies, language],
  )

  return (
    <TableWrapper>
      <TableHeader title="Summary" apiRef={apiRef} />
      <DataGridPremium
        {...gridProps}
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        loading={loading}
        sx={{
          ...gridProps.sx,
          '& .MuiDataGrid-columnHeader': { backgroundColor: ColorsDeprecated.surfaceOnWhite },
        }}
      />
    </TableWrapper>
  )
}

export default VouchersSummaryTable
